/* eslint-disable react/no-unstable-nested-components */
import Accordion from '@/components/Accordion/Accordion';
import Checkbox from '@/components/Checkbox/Checkbox';
import Link from '@/components/Link/Link';
import { useSTKContext } from '@/context/context';
import { Store } from '@/types/stores.types';
import { RowSelectionState } from '@tanstack/react-table';
import _ from 'lodash/';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StoreGroupTableProps } from '../../StoreGroupsTabItemProps.types';
import SimpleTable from '../ReactGroupTable/SimpleTable';
import styles from './StoreGroup.module.css';
import { RowWithStoreGroupCount } from '../../../StoresTabItem/StoresTabItemProps.types';

const provideNewSelectedRows = (selectedRows: RowSelectionState | undefined, stores: Store[]) => {
  const selectedStores = new Set(Object.keys(selectedRows as RowSelectionState));
  const newSelectedRows: string[] = [];
  selectedStores.forEach((index) => {
    newSelectedRows.push(stores[+index].storeNo);
  });
  return newSelectedRows;
};

const ViewMenu = ({ rowWrapper, countByStoreGroup, storeLinkTo }: RowWithStoreGroupCount) => {
  const { setStoresAndGroups } = useSTKContext();
  const { row } = rowWrapper;
  const linkTo = storeLinkTo ?? `/menus/${row.original.storeNo}`;
  return (
    <Link
      onClick={() =>
        setStoresAndGroups({
          origin: 'tab-store',
          stores: [
            {
              storeNo: row.original.storeNo,
              storeGroup: row.original.groupName,
            },
          ],
          countByStoreGroup,
        })
      }
      to={linkTo}
    >
      {row.original.storeNo}
    </Link>
  );
};

export const StoreGroupTable: FC<StoreGroupTableProps> = ({
  name = '',
  stores,
  onStoresSelectionChange,
  storeLinkTo,
}) => {
  const { t } = useTranslation();

  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [allSelected, setAllSelected] = useState(false);
  const [checked, setChecked] = useState(false);
  const [storeGroups, setStoreGroups] = useState<Set<string>>(new Set());
  const storesByGroups = _.groupBy(stores, 'groupName');
  const countByStoreGroup = Object.entries(storesByGroups).map(([groupName, storeArray]) => ({
    storeGroup: groupName,
    count: storeArray.length,
  }));

  useEffect(() => {
    onStoresSelectionChange(provideNewSelectedRows(selectedRows, stores));
    if (Object.keys(selectedRows).length > 0 && Object.keys(selectedRows).length === stores.length) {
      storeGroups.add(name);
      setAllSelected(true);
      setChecked(true);
    } else {
      storeGroups.delete(name);
      setChecked(false);
    }
  }, [selectedRows]);

  const handleCheckboxChange = () => {
    const newAllSelected = !checked;
    if (newAllSelected) {
      storeGroups.add(name);
    } else {
      storeGroups.delete(name);
    }
    setChecked(newAllSelected);
    if (newAllSelected === true) {
      setAllSelected(false);
    }
    // TO FIX after when we had a better idea
    _.delay(() => setAllSelected(newAllSelected), 1);
  };

  const handleCheckboxClick = () => {
    if (checked === false) {
      setAllSelected(false);
    }
  };

  return (
    <Accordion
      key={name}
      title={
        <div className={styles.accordionTitle}>
          <Checkbox
            label=""
            checked={checked}
            onClick={() => handleCheckboxClick()}
            onChange={() => handleCheckboxChange()}
            aria-label="select all"
            id={`checkbox_${name}`}
            data-testid={`checkbox_${name}`}
          />
          <div className={styles.groupName}>{name}</div>
          <div className={styles.storesCount}>{t('menu.storesLabel', { count: stores?.length })}</div>
        </div>
      }
    >
      <SimpleTable
        className={styles.cardWidth}
        selectAllRows={allSelected}
        enableRowSelection
        selectedRows={selectedRows}
        onRowSelectionChange={setSelectedRows}
        returnedRecordsText={(count) => t('menu.table.returnedRecord', { count })}
        data={stores}
        columns={[
          {
            header: t('menu.table.storeId'),
            accessorKey: 'storeNo',
            enableSorting: false,
            cell: (rowWrapper) => (
              <ViewMenu rowWrapper={rowWrapper} countByStoreGroup={countByStoreGroup} storeLinkTo={storeLinkTo} />
            ),
          },
          {
            header: t('menu.table.address'),
            accessorFn: (row: Store) => `${row.address}, ${row.city} ${row.state} ${row.zip}`,
            enableSorting: false,
          },
        ]}
      />
    </Accordion>
  );
};
